import React from 'react';
import {
  AccountListModel,
  DefaultAccountListModel,
  OEMAccountListModel,
  ACCOUNT_SORT_FIELD_MAPPING,
  AccountListColumns
} from 'components/AccountList';
import { AccountManager, Account } from 'core';
import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { SearchBarModel, SearchBar } from 'components/common/SearchBar';
import { toast } from 'react-toastify';
import i18n from 'i18n';
import _ from 'lodash';
import { Pagination } from 'core/pagination/Pagination';

const SIZE_PER_PAGE = 10;

export type AccountsHomeState = {
  readonly loading: boolean;
  readonly error: Error | null;
  readonly selectAgency?: number;
  readonly searchString: string;
  readonly selectedAccounts: number[];
  readonly pagination: Pagination;
};

export interface AccountsHomeModel extends SearchBarModel {
  refresh(): Promise<void>;
  getAccountListModel(): AccountListModel;
  onAgencyChange(agencyId): void;
  handleRemoveSelect(): void;
  activateAccounts(event: any): Promise<void>;
  deactivateAccounts(event: any): Promise<void>;

  readonly agencyOptions?: SelectOptions[];
  readonly state: AccountsHomeState;
  readonly event: UpdateEventListener<AccountsHomeModel>;
  readonly rootPath: string;
  readonly searchBarRef: React.RefObject<SearchBar>;
}

export type AccountsHomeProps = {
  readonly model: AccountsHomeModel;
};

export class DefaultAccountsHomeModel implements AccountsHomeModel {
  loading: boolean;
  error: Error | null;
  searchBarRef: React.RefObject<SearchBar> = React.createRef<SearchBar>();
  event: FireableUpdateEventListener<AccountsHomeModel>;
  searchString = '';
  selectedAccounts: number[] = [];
  pagination: Pagination = { page: 1, size: SIZE_PER_PAGE, totalCount: 0 };
  sortField: string;
  sortOrder: 'asc' | 'desc' = 'desc';
  debouncedFetch = _.debounce(this.fetchAccounts.bind(this), 1000);

  manager: AccountManager;
  accounts: Array<Account> = [];
  rootPath = '/accounts';

  constructor(
    manager: AccountManager,
    protected isSysAdmin: boolean
  ) {
    this.manager = manager;
    this.error = null;
    this.loading = false;
    this.sortField = AccountListColumns.ID;
    this.event = new FireableUpdateEventListener<AccountsHomeModel>();
  }

  get state(): AccountsHomeState {
    return {
      error: this.error,
      loading: this.loading,
      searchString: this.searchString,
      selectedAccounts: this.selectedAccounts,
      pagination: this.pagination
    };
  }

  get placeholder(): string {
    return 'accounts.home.searchPlaceholder';
  }

  get defaultValue(): string {
    return this.searchString;
  }

  getAccountListModel(): AccountListModel {
    return new DefaultAccountListModel(
      this.accounts,
      this.isSysAdmin,
      this.selectedAccounts,
      this.pagination,
      this.onTableChange,
      this.handleOnSelect,
      this.handleOnSelectAll,
      this.handleRemoveSelect,
      this.activateAccounts,
      this.deactivateAccounts
    );
  }

  handleOnSelect = (account: Account) => {
    const accountId = account.id;
    if (this.selectedAccounts.indexOf(accountId) > -1) {
      _.remove(this.selectedAccounts, (id) => id === accountId);
    } else {
      this.selectedAccounts.push(accountId);
    }
    this.notify(false);
  };

  handleOnSelectAll = () => {
    const currentPageIds = this.accounts.map((account) => account.id);
    const allSelected = currentPageIds.every((id) =>
      this.selectedAccounts.includes(id)
    );
    this.selectedAccounts = allSelected ? [] : currentPageIds;
    this.notify(false);
  };

  handleRemoveSelect = () => {
    this.selectedAccounts = [];
    this.notify(false);
  };

  activateAccounts = async (event: any): Promise<void> => {
    event && event.stopPropagation();
    await this.executeAccountsAction(
      'activate',
      i18n.t('accounts.messages.activateSuccess')
    );
  };

  deactivateAccounts = async (event: any): Promise<void> => {
    event && event.stopPropagation();
    await this.executeAccountsAction(
      'deactivate',
      i18n.t('accounts.messages.deactivateSuccess')
    );
  };

  executeAccountsAction = async (
    action: 'activate' | 'deactivate',
    successMessage: string
  ) => {
    this.notify(true);
    try {
      await this.manager.updateAccountsState(this.selectedAccounts, action);
      this.selectedAccounts = [];
      await this.fetchAccounts();
      toast.success(successMessage);
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.notify(false);
    }
  };

  async refresh(): Promise<void> {
    this.pagination = { page: 1, size: SIZE_PER_PAGE, totalCount: 0 };
    this.selectedAccounts = [];
    this.searchString = '';
    this.searchBarRef.current?.clear();
    await this.fetchAccounts();
  }

  async fetchAccounts(): Promise<void> {
    this.notify(true);
    try {
      const apiSortField =
        ACCOUNT_SORT_FIELD_MAPPING[this.sortField] || this.sortField;
      const result = await this.manager.getAccountsWithPagination(
        this.pagination.page,
        this.pagination.size,
        apiSortField,
        this.sortOrder,
        this.searchString,
        []
      );
      this.accounts = result.accounts;
      this.pagination = result.pagination;
      this.notify(false);
    } catch (error) {
      this.notify(false, error as Error);
    }
  }

  search(keyword: string) {
    this.searchString = keyword;
    this.pagination = { ...this.pagination, page: 1 };
    this.selectedAccounts = [];
    this.debouncedFetch();
  }

  onTableChange = (type: string, props: any) => {
    if (type === 'pagination') {
      this.pagination = { ...this.pagination, page: props.page };
      this.selectedAccounts = [];
      this.fetchAccounts();
    } else if (type === 'sort') {
      this.sortField = props.sortField;
      this.sortOrder = props.sortOrder;
      this.pagination = { ...this.pagination, page: 1 };
      this.selectedAccounts = [];
      this.fetchAccounts();
    }
  };

  onAgencyChange(_) {
    /* no-op */
  }

  notify(loading: boolean, error: Error | null = null) {
    this.error = error;
    this.loading = loading;
    this.event.fireEvent(this);
  }
}

export class OEMAccountsHomeModel extends DefaultAccountsHomeModel {
  rootPath = '/oem-accounts';
  selectAgency?: number;
  searchBarRef: React.RefObject<SearchBar> = React.createRef<SearchBar>();

  constructor(
    public agencyOptions: SelectOptions[],
    manager: AccountManager,
    isSysAdmin: boolean
  ) {
    super(manager, isSysAdmin);
  }

  getAccountListModel(): AccountListModel {
    return new OEMAccountListModel(
      this.accounts,
      this.selectedAccounts,
      this.pagination,
      this.onTableChange,
      this.handleOnSelect,
      this.handleOnSelectAll,
      this.handleRemoveSelect,
      this.activateAccounts,
      this.deactivateAccounts
    );
  }

  get state(): AccountsHomeState {
    return {
      error: this.error,
      loading: this.loading,
      selectAgency: this.selectAgency,
      searchString: this.searchString,
      selectedAccounts: this.selectedAccounts,
      pagination: this.pagination
    };
  }

  async fetchAccounts(): Promise<void> {
    this.notify(true);
    try {
      const apiSortField =
        ACCOUNT_SORT_FIELD_MAPPING[this.sortField] || this.sortField;
      const result = await this.manager.getOemAccountsWithPagination(
        this.pagination.page,
        this.pagination.size,
        apiSortField,
        this.sortOrder,
        this.searchString,
        [],
        this.selectAgency
      );
      this.accounts = result.accounts;
      this.pagination = result.pagination;
      this.notify(false);
    } catch (error) {
      this.notify(false, error as Error);
    }
  }

  onAgencyChange = (agencyId) => {
    this.selectAgency = agencyId;
    this.pagination = { ...this.pagination, page: 1 };
    this.selectedAccounts = [];
    this.fetchAccounts();
  };
}
